import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
} from "react";

import { REGULATORY_CATEGORY_OTC } from "@/components/care/constants";
import { useCareEligibilityContext } from "@/contexts/careEligibilityContext";
import { ConsultsContext } from "@/contexts/consultsContext";
import { useLatestReadyTestContext } from "@/contexts/latestReadyTestContext";
import { useLightweightAllAvailableTreatments } from "@/hooks/care/transparentCare/useLightweightAllAvailableTreatments";
import { useLightweightTransparentCareTreatments } from "@/hooks/care/transparentCare/useLightweightTransparentCareTreatments";
import { useTransparentCareTreatments } from "@/hooks/care/transparentCare/useTransparentCareTreatments";
import { useProducts } from "@/hooks/useProducts";
import { CareEligibilityState } from "@/pages/shopAll/types";
import {
  LightweightAllAvailableTreatments,
  LightweightTransparentCareRecommendations,
  Prescription,
  TransparentCareRecommendations,
} from "@/types/care";

export type TransparentCareTreatmentsContextType = {
  transparentCareTreatments?: TransparentCareRecommendations;
  transparentCareTreatmentSlugs?: LightweightTransparentCareRecommendations;
  allAvailableTreatments?: LightweightAllAvailableTreatments;
  loading: boolean;
  lightweightLoading: boolean;
  allAvailableTreatmentsLoading: boolean;
  error: Error | null;
  lightweightError: Error | null;
  allAvailableTreatmentsError: Error | null;
  refetchTransparentCareTreatments: () => void;
  refetchLightweightTransparentCareTreatments: () => void;
  refetchAllAvailableTreatments: () => void;
  aLaCareTreatmentSlugs: Set<string>;
  availableProducts: Prescription[];
  isLocked: (product: Prescription) => boolean;
  isUnavailable: (product: Prescription) => boolean;
};

export const TransparentCareTreatmentsContext =
  createContext<TransparentCareTreatmentsContextType>({
    loading: false,
    lightweightLoading: true,
    allAvailableTreatmentsLoading: true,
    error: null,
    lightweightError: null,
    allAvailableTreatmentsError: null,
    refetchTransparentCareTreatments: () => {},
    refetchLightweightTransparentCareTreatments: () => {},
    refetchAllAvailableTreatments: () => {},
    aLaCareTreatmentSlugs: new Set<string>(),
    availableProducts: [],
    isLocked: () => false,
    isUnavailable: () => false,
  });

export const TransparentCareTreatmentsProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { vaginalTest } = useLatestReadyTestContext();
  const { consultComputations } = useContext(ConsultsContext);
  const { latestConsultInProgress } = consultComputations;
  const { allProducts } = useProducts();
  const { careEligibilityState } = useCareEligibilityContext();

  // Use vaginal test hash for CTP bundle
  // This ensures dual eligible users (with both vaginal and urine tests) get CTP bundle
  const testHashForCTP = vaginalTest?.hash;

  // TODO: This will be refactored to only use lightweight endpoints
  // We no longer will return entire product data in treatment endpoints
  const {
    transparentCareTreatments,
    error,
    isLoading: loading,
    refetchTransparentCareTreatments,
  } = useTransparentCareTreatments(testHashForCTP);

  const {
    lightweightTransparentCareTreatments,
    error: lightweightError,
    isLoading: lightweightLoading,
    refetchLightweightTransparentCareTreatments,
  } = useLightweightTransparentCareTreatments(testHashForCTP);

  const {
    lightweightAllAvailableTreatments,
    error: lightweightAllAvailableTreatmentsError,
    isLoading: lightweightAllAvailableTreatmentsLoading,
    refetchAllAvailableTreatments,
  } = useLightweightAllAvailableTreatments();

  // Extract all treatment slugs from a_la_care_treatments
  const allAvailableTreatmentSlugs = useMemo(() => {
    if (!lightweightAllAvailableTreatments?.all_available_treatments) {
      return new Set<string>();
    }

    const slugs = new Set<string>();
    Object.values(
      lightweightAllAvailableTreatments.all_available_treatments
    ).forEach((phase) => {
      phase.treatment_slugs.forEach((slug) => slugs.add(slug));
    });

    // Add male partner voucher product slug if it exists
    if (lightweightAllAvailableTreatments?.male_partner_voucher_product_slug) {
      slugs.add(
        lightweightAllAvailableTreatments.male_partner_voucher_product_slug
      );
    }

    return slugs;
  }, [
    lightweightAllAvailableTreatments?.all_available_treatments,
    lightweightAllAvailableTreatments?.male_partner_voucher_product_slug,
  ]);

  // CPG products sit in Routine Essentials, not with the Rx treatments.
  const availableProducts = useMemo(() => {
    return Object.values(allProducts?.care_products || {}).filter(
      (product) =>
        allAvailableTreatmentSlugs.has(product.slug) &&
        product.regulatory_category !== REGULATORY_CATEGORY_OTC
    );
  }, [allProducts?.care_products, allAvailableTreatmentSlugs]);

  // Until the server answers, fall back to the local consult list rather than unlocking.
  const consultInProgress = careEligibilityState
    ? careEligibilityState === CareEligibilityState.CONSULT_IN_PROGRESS
    : !!latestConsultInProgress;
  const isLocked = useCallback(
    (product: Prescription) =>
      consultInProgress &&
      (latestConsultInProgress?.selected_treatments.some(
        (treatment) => treatment.slug === product.slug
      ) ||
        !latestConsultInProgress?.is_pre_submit),
    [consultInProgress, latestConsultInProgress]
  );

  const isUnavailable = useCallback(
    (product: Prescription) =>
      !availableProducts.some(
        (availableProduct) => availableProduct.slug === product.slug
      ),
    [availableProducts]
  );

  return (
    <TransparentCareTreatmentsContext.Provider
      value={{
        transparentCareTreatments,
        transparentCareTreatmentSlugs: lightweightTransparentCareTreatments,
        allAvailableTreatments: lightweightAllAvailableTreatments,
        loading,
        lightweightLoading,
        allAvailableTreatmentsLoading: lightweightAllAvailableTreatmentsLoading,
        error,
        lightweightError,
        allAvailableTreatmentsError: lightweightAllAvailableTreatmentsError,
        refetchTransparentCareTreatments,
        refetchLightweightTransparentCareTreatments,
        refetchAllAvailableTreatments,
        aLaCareTreatmentSlugs: allAvailableTreatmentSlugs,
        availableProducts,
        isLocked,
        isUnavailable,
      }}
    >
      {children}
    </TransparentCareTreatmentsContext.Provider>
  );
};

export const useTransparentCareTreatmentsContext = () => {
  const context = useContext(TransparentCareTreatmentsContext);
  if (!context) {
    throw new Error(
      "Transparent Care Treatments Context must be used within the TransparentCareTreatmentsProvider"
    );
  }

  return context;
};
